import { useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import moment from 'moment'
import InputDropdown from '@/components/InputDropdown/index'
import DatePicker from '@/components/DatePicker/index'
import { FILTER_TYPE, PICKED_DATE_FORMAT } from '../listing-config'
import { statusLabel } from '../constants'

// Selections hold the picked day, not the instant sent to the API — the picker
// reads them back, and `to` would otherwise display the following day.
const DISPLAY_DATE_FORMAT = 'DD/MM/YYYY'

/**
 * Option lists arrive in two shapes. `main_contractors` and `statuses` are flat
 * string arrays; `project_contracts` is an array of objects, which is why a
 * filter can declare `optionValue` / `optionLabel` to index into them.
 *
 * Note two filters may point at the same `optionsFrom` with different
 * `optionLabel`s — the contract number and name dropdowns both read
 * `project_contracts`. Where a label maps to several values (one project name
 * spanning two contract numbers) the list will show that label more than once;
 * the values differ, so selecting either is meaningful and buildFilterParams
 * dedupes the resulting params.
 */
const toOptions = (filter, filterData, selected, secondaryApproval) => {
  const raw = filterData[filter.optionsFrom]
  if (!Array.isArray(raw)) return []

  const chosen = new Set(Array.isArray(selected) ? selected : [])
  // project_contracts is distinct per {number, name} pair, so reading one field
  // repeats it. Keyed on value *and* label — one number spans several names,
  // and those are distinct options.
  const seen = new Set()

  return raw
    .map(entry => {
      const isObject = entry !== null && typeof entry === 'object'
      const id = isObject ? entry[filter.optionValue] : entry
      if (id === undefined || id === null || id === '') return null

      let label = isObject ? entry[filter.optionLabel] : entry
      // Statuses are stored as enum values but read as sentences — and the
      // second-stage ones are named after the approving org, which only the
      // data element's config knows.
      if (filter.optionsFrom === 'statuses') {
        label = statusLabel(id, secondaryApproval)
      }

      const text = String(label ?? id)
      // Stringified, not concatenated: names contain spaces, so "A B"+"C"
      // must not equal "A"+"B C".
      const key = JSON.stringify([id, text])
      if (seen.has(key)) return null
      seen.add(key)

      return { id, label: text, $selected: chosen.has(id) }
    })
    .filter(Boolean)
}

const parseDate = value => {
  if (!value) return null
  const parsed = moment(value, PICKED_DATE_FORMAT, true)
  return parsed.isValid() ? parsed.toDate() : null
}

const FilterBar = ({
  filters = [],
  filterData = {},
  selections = {},
  onSelect,
  secondaryApproval,
}) => {
  // Per-dropdown search text, owned here because it is presentation state and
  // must not feed back into the request.
  const [searchText, setSearchText] = useState({})

  if (!filters.length) return null

  const setBound = (filter, bound) => date => {
    const current = selections[filter.key] || {}
    onSelect(filter.key, {
      ...current,
      [bound]: date ? moment(date).format(PICKED_DATE_FORMAT) : undefined,
    })
  }

  // Date ranges sit on a row of their own beneath the dropdowns, so they go last
  // regardless of config order — grid auto-placement would otherwise push any
  // dropdown after them onto the date row.
  const isRange = filter => filter.type === FILTER_TYPE.DATE_RANGE
  const dropdowns = filters.filter(filter => !isRange(filter))
  const ordered = [...dropdowns, ...filters.filter(isRange)]
  // A range spans two columns, so the grid needs at least two; past four,
  // dropdowns wrap to another row rather than shrinking their placeholders.
  const columns = Math.min(Math.max(dropdowns.length, 2), 4)

  return (
    // At 1200px and up this is pitstop's filter row: one column per dropdown,
    // 12px apart, with any date range starting a row of its own beneath them.
    // The column count is set from the config rather than fixed, because the
    // number of filters differs per data element.
    //
    // Below 1200px it is a grid on the same ladder the EMS listing uses
    // (SubmittalsListingPage.jsx) — one column under 600px, two above. A flex row
    // cannot do this: every column here is `flex-[1_1_0] min-w-0` and the
    // dropdown inside resolves to `w-full` with no intrinsic minimum, so the line
    // never overflows and `flex-wrap` never fires. Five filters simply divided the
    // width, which at 390px is ~66px each.
    //
    // Aligned to the start, not the centre as pitstop does: Input always renders
    // an empty caption slot below its field, so a dropdown column is 40px tall
    // against the picker's 36px, and centring drops the pickers 2px low. Aligning
    // tops puts the two 36px fields on the same line and leaves the caption slot
    // hanging harmlessly below — which also holds if a caption or error ever
    // fills it, where centring would shift the fields again.
    <div
      data-testid="submittals-filter-row"
      className="grid w-full grid-cols-1 items-start gap-2 phone:grid-cols-2 tablet-p:gap-3 tablet:grid-cols-[repeat(var(--submittals-filter-columns),minmax(0,1fr))]"
      style={{ '--submittals-filter-columns': columns }}
    >
      {ordered.map(filter => {
        const isDateRange = isRange(filter)
        return (
          <div
            key={filter.key}
            data-testid={`submittals-filter-col-${filter.key}`}
            // Two pickers need the room of two dropdowns: the whole line in the
            // two-column grid, and the first two columns of a fresh row above
            // 1200px.
            className={clsx('min-w-0', isDateRange && 'phone:col-span-2 tablet:col-start-1')}
          >
            {isDateRange ? (
              // Two equal columns with the row's own gap, so each picker is as
              // wide as the dropdown above it; the separator sits in the gap.
              <div className="relative grid grid-cols-2 items-center gap-2 tablet-p:gap-3">
                <DatePicker
                  dataTestId={`submittals-filter-${filter.key}-from`}
                  selectDate={parseDate(selections[filter.key]?.from)}
                  setSelectDate={setBound(filter, 'from')}
                  placeHolderText="Select Start Date"
                  dateFormat={DISPLAY_DATE_FORMAT}
                />
                <span className="text-grey-50 pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                  –
                </span>
                <DatePicker
                  dataTestId={`submittals-filter-${filter.key}-to`}
                  selectDate={parseDate(selections[filter.key]?.to)}
                  setSelectDate={setBound(filter, 'to')}
                  placeHolderText="Select End Date"
                  dateFormat={DISPLAY_DATE_FORMAT}
                />
              </div>
            ) : (
              (() => {
                const options = toOptions(
                  filter,
                  filterData,
                  selections[filter.key],
                  secondaryApproval
                )
                const selectedOptions = options.filter(option => option.$selected)
                return (
                  <InputDropdown
                    id={`submittals-filter-${filter.key}-id`}
                    name={filter.key}
                    placeholder={selectedOptions.length > 0 ? ' ' : `Select ${filter.label}`}
                    dropdownOption={options}
                    dropdownSelectedOption={selectedOptions}
                    onSelectDropdownItem={items =>
                      onSelect(
                        filter.key,
                        (items || []).map(item => item.id)
                      )
                    }
                    value={searchText[filter.key] || ''}
                    onChange={text =>
                      setSearchText(prev => ({ ...prev, [filter.key]: text ?? '' }))
                    }
                    multipleSelection
                    fullWidth
                  />
                )
              })()
            )}
          </div>
        )
      })}
    </div>
  )
}

FilterBar.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
      type: PropTypes.string,
      optionsFrom: PropTypes.string,
      optionValue: PropTypes.string,
      optionLabel: PropTypes.string,
    })
  ),
  filterData: PropTypes.object,
  selections: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  secondaryApproval: PropTypes.shape({
    required: PropTypes.bool,
    label: PropTypes.string,
  }),
}

export default FilterBar
